import { z } from "zod";

export type FontRef = { family: string; source: "system" | "upload"; files?: string[] };

export type ThemeTokens = {
  colors: {
    primary: string;
    accent: string;
    text: string;
    muted: string;
    code: string;
    border: string;
    stripe: string;
    caption: string;
    noteFill: string;
    warnFill: string;
    white: string;
  };
  fonts: { body: FontRef; mono: FontRef };
  sizes: {
    body: number;
    table: number;
    group: number;
    groupTitle: number;
    groupMeta: number;
    code: number;
    small: number;
    title: number;
    h1: number;
    h2: number;
    coverSubtitle: number;
    coverDocType: number;
  };
  page: { size: "A4"; marginMm: number; headerMm: number; footerMm: number };
  spacing: {
    after: Record<"h1" | "h2" | "p" | "kv" | "bullets" | "steps" | "code", number>;
    firstBefore: { h1: number; h2: number };
    gap: {
      afterH1: number;
      afterH2: number;
      h1AfterTable: number;
      h1AfterText: number;
      h2AfterTable: number;
      h2AfterText: number;
      pAfterP: number;
      textAfterText: number;
      aroundTable: number;
    };
  };
  table: { headerFill: "primary"; stripes: boolean; borderColor: "border"; cellPadding: { v: number; h: number } };
  logos: { cover: string; header: string };
  labels: Record<
    "en" | "nl",
    {
      internal: string;
      clientCover: string;
      clientFooter: string;
      page: string;
      contents: string;
      note: string;
      warning: string;
    }
  >;
};

export type Layout = {
  key: string;
  name: string;
  cover?: string;
  titleBlock?: string;
  header: string;
  footer: string;
  css: string;
};
export type ThemeVersionData = { tokens: ThemeTokens; layouts: Record<string, Layout>; advancedCss: string };

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "expected #RRGGBB");
const pt = z.number().positive().max(400);
const space = z.number().min(0).max(400);
const label = z
  .string()
  .min(1)
  .max(200)
  // biome-ignore lint/suspicious/noControlCharactersInRegex: rejecting control characters is the point
  .regex(/^[^\u0000-\u001f\u007f]*$/, "control characters are not allowed");
const family = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9 ._-]+$/, "font family may only contain letters, digits, space, dot, underscore and hyphen");
const attRef = z.string().regex(/^att:[0-9a-f]{64}$/, 'expected "att:<sha256>"');
const logoRef = z
  .string()
  .regex(/^(att:[0-9a-f]{64}|builtin:klarity-logo)$/, 'expected "att:<sha256>" or "builtin:klarity-logo"');

export const FontRefSchema: z.ZodType<FontRef> = z
  .strictObject({ family, source: z.enum(["system", "upload"]), files: z.array(attRef).min(1).max(4).optional() })
  .refine((f) => f.source === "system" || (f.files?.length ?? 0) > 0, {
    message: "uploaded fonts need at least one file",
  });

const labelSet = z.strictObject({
  internal: label,
  clientCover: label.refine((s) => s.includes("{client}"), { message: "must contain {client}" }),
  clientFooter: label,
  page: label,
  contents: label,
  note: label,
  warning: label,
});

export const ThemeTokensSchema: z.ZodType<ThemeTokens> = z.strictObject({
  colors: z.strictObject({
    primary: hex,
    accent: hex,
    text: hex,
    muted: hex,
    code: hex,
    border: hex,
    stripe: hex,
    caption: hex,
    noteFill: hex,
    warnFill: hex,
    white: hex,
  }),
  fonts: z.strictObject({ body: FontRefSchema, mono: FontRefSchema }),
  sizes: z.strictObject({
    body: pt,
    table: pt,
    group: pt,
    groupTitle: pt,
    groupMeta: pt,
    code: pt,
    small: pt,
    title: pt,
    h1: pt,
    h2: pt,
    coverSubtitle: pt,
    coverDocType: pt,
  }),
  page: z.strictObject({
    size: z.literal("A4"),
    marginMm: z.number().min(5).max(60),
    headerMm: z.number().min(0).max(60),
    footerMm: z.number().min(0).max(60),
  }),
  spacing: z.strictObject({
    after: z.strictObject({ h1: space, h2: space, p: space, kv: space, bullets: space, steps: space, code: space }),
    firstBefore: z.strictObject({ h1: space, h2: space }),
    gap: z.strictObject({
      afterH1: space,
      afterH2: space,
      h1AfterTable: space,
      h1AfterText: space,
      h2AfterTable: space,
      h2AfterText: space,
      pAfterP: space,
      textAfterText: space,
      aroundTable: space,
    }),
  }),
  table: z.strictObject({
    headerFill: z.literal("primary"),
    stripes: z.boolean(),
    borderColor: z.literal("border"),
    cellPadding: z.strictObject({ v: space, h: space }),
  }),
  logos: z.strictObject({ cover: logoRef, header: logoRef }),
  labels: z.strictObject({ en: labelSet, nl: labelSet }),
});

const templateText = z.string().max(20_000);

export const LayoutSchema: z.ZodType<Layout> = z.strictObject({
  key: z.string().regex(/^[a-z][a-z0-9-]{0,31}$/, "layout key: lowercase letters, digits and hyphens"),
  name: z.string().min(1).max(64),
  cover: templateText.optional(),
  titleBlock: templateText.optional(),
  header: templateText,
  footer: templateText,
  css: z.string().max(100_000),
});

export const ThemeVersionDataSchema: z.ZodType<ThemeVersionData> = z
  .strictObject({
    tokens: ThemeTokensSchema,
    layouts: z.record(z.string(), LayoutSchema),
    advancedCss: z.string().max(100_000),
  })
  .refine((d) => Object.keys(d.layouts).length > 0, { message: "at least one layout is required", path: ["layouts"] })
  .refine((d) => Object.entries(d.layouts).every(([k, l]) => k === l.key), {
    message: "every layout's key must equal its map key",
    path: ["layouts"],
  });
